/**
 * Document Imports Vuex Actions
 * Async operations for the document import feature.
 */

import documentImportService from '../services/documentImportService';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { resolveDocumentNameConflicts, reportVersionUploads } from '@/features/Documents/utils/nameConflictResolver';

export default {
  /**
   * Fetch documents with filters and pagination
   */
  async FETCH_DOCUMENTS({ commit, state }, params = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const queryParams = {
        page: params.page || state.tableOptions.page,
        itemsPerPage: params.itemsPerPage || state.tableOptions.itemsPerPage,
        sortBy: params.sortBy || state.tableOptions.sortBy?.[0] || 'createDate',
        sortDesc: params.sortDesc ?? state.tableOptions.sortDesc?.[0] ?? true,
      };

      // Add scope filter (mutually exclusive)
      if (state.scopeType === 'Facility' && state.selectedFacilityId) {
        queryParams.assetId = state.selectedFacilityId;
      } else if (state.scopeType === 'FacilityGroup' && state.selectedFacilityGroupId) {
        queryParams.facilityGroupId = state.selectedFacilityGroupId;
      }

      // Add document type filter
      if (params.documentTypeId) {
        queryParams.documentTypeId = params.documentTypeId;
      }

      const response = await documentImportService.getDocuments(queryParams);

      commit('SET_DOCUMENTS', response.items || []);
      commit('SET_DOCUMENTS_TOTAL', response.total || 0);

      debugLog('[DocumentImports]', 'Fetched documents:', response.total);
      return response;
    } catch (err) {
      debugError('[DocumentImports]', 'Failed to fetch documents:', err);
      commit('SET_ERROR', err.message || 'Failed to fetch documents');
      throw err;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Load document types for dropdown
   */
  async LOAD_DOCUMENT_TYPES({ commit, state }) {
    if (state.documentTypesLoaded && state.documentTypes.length > 0) {
      return state.documentTypes;
    }

    try {
      const types = await documentImportService.getDocumentTypes();
      commit('SET_DOCUMENT_TYPES', types);
      debugLog('[DocumentImports]', 'Loaded document types:', types.length);
      return types;
    } catch (err) {
      debugError('[DocumentImports]', 'Failed to load document types:', err);
      throw err;
    }
  },

  /**
   * Upload files to blob storage (returns storage references)
   */
  async UPLOAD_FILES({ commit }, files) {
    commit('SET_UPLOADING', true);
    commit('SET_ERROR', null);

    try {
      const result = await documentImportService.uploadFiles(files);
      debugLog('[DocumentImports]', 'Uploaded files:', result.length);
      return result;
    } catch (err) {
      debugError('[DocumentImports]', 'Failed to upload files:', err);
      commit('SET_ERROR', err.message || 'Failed to upload files');
      throw err;
    } finally {
      commit('SET_UPLOADING', false);
    }
  },

  /**
   * Create documents with metadata (bulk import)
   */
  async CREATE_DOCUMENTS({ commit, dispatch, state }, documents) {
    commit('SET_IMPORTING', true);
    commit('SET_ERROR', null);

    try {
      const { data: result, headers } = await documentImportService.createDocumentsWithResponse(documents);
      // Say what happened to files sent as new versions (now vN / pinned version stays / identical).
      reportVersionUploads(documents, result, headers);

      commit('SET_IMPORT_RESULT', {
        status: 'Success',
        successCount: result.length,
        documents: result,
      });

      // Refresh documents list
      await dispatch('FETCH_DOCUMENTS');

      debugLog('[DocumentImports]', 'Created documents:', result.length);
      return result;
    } catch (err) {
      debugError('[DocumentImports]', 'Failed to create documents:', err);
      commit('SET_ERROR', err.message || 'Failed to create documents');
      commit('SET_IMPORT_RESULT', {
        status: 'Error',
        error: err.message,
      });
      throw err;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Import pending files (upload + create documents)
   */
  async IMPORT_PENDING_FILES({ commit, dispatch, state }) {
    if (!state.pendingFiles.length) {
      throw new Error('No files to import');
    }

    commit('SET_IMPORTING', true);
    commit('SET_ERROR', null);

    try {
      // Step 0: Names already taken in the facility (or group): new version, another name, or skip
      const isGroup = state.scopeType === 'FacilityGroup';
      const drafts = await resolveDocumentNameConflicts(
        state.pendingFiles.map((pf, index) => ({
          documentNumber: pf.documentNumber,
          documentTitle: pf.documentTitle,
          fileName: pf.fileName,
          pendingIndex: index,
        })),
        isGroup ? { facilityGroupId: state.selectedFacilityGroupId } : { assetId: state.selectedFacilityId },
      );
      if (drafts === null) return null; // cancelled: keep the pending files
      if (drafts.length === 0) {
        commit('CLEAR_PENDING_FILES');
        return [];
      }
      const kept = drafts.map(draft => state.pendingFiles[draft.pendingIndex]);

      // Step 1: Upload files to blob storage
      const files = kept.map(pf => pf.file);
      const uploadedFiles = await dispatch('UPLOAD_FILES', files);

      // Step 2: Map uploaded files to document records
      const documents = kept.map((pf, index) => {
        const uploaded = uploadedFiles[index];
        const draft = drafts[index];
        return {
          documentNumber: draft.documentNumber,
          documentTitle: draft.documentTitle,
          documentTypeId: pf.documentTypeId || state.selectedDocumentTypeId,
          revisionNumber: pf.revisionNumber || '0',
          storageReference: uploaded.storageReference,
          fileName: draft.fileName || uploaded.fileName,
          onNameConflict: draft.onNameConflict,
          targetDocumentId: draft.targetDocumentId,
          // New version only: whether the links on the current version are copied onto it.
          transferLinks: draft.transferLinks === true,
          size: uploaded.size,
          // From the upload response; lets the API detect duplicate uploads
          contentHash: uploaded.contentHash,
          contentType: uploaded.contentType,
          folderName: 'MasterDocs',
          // Scope (mutually exclusive)
          assetId: state.scopeType === 'Facility' ? state.selectedFacilityId : null,
          facilityGroupId: state.scopeType === 'FacilityGroup' ? state.selectedFacilityGroupId : null,
        };
      });

      // Step 3: Create document records
      const result = await dispatch('CREATE_DOCUMENTS', documents);

      // Step 4: Clear pending files on success
      commit('CLEAR_PENDING_FILES');

      return result;
    } catch (err) {
      debugError('[DocumentImports]', 'Import failed:', err);
      throw err;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Delete documents by IDs
   */
  async DELETE_DOCUMENTS({ commit, dispatch }, ids) {
    commit('SET_DELETING', true);
    commit('SET_ERROR', null);

    try {
      await documentImportService.deleteDocuments(ids);

      // Refresh documents list
      await dispatch('FETCH_DOCUMENTS');

      debugLog('[DocumentImports]', 'Deleted documents:', ids.length);
      return true;
    } catch (err) {
      debugError('[DocumentImports]', 'Failed to delete documents:', err);
      commit('SET_ERROR', err.message || 'Failed to delete documents');
      throw err;
    } finally {
      commit('SET_DELETING', false);
    }
  },

  /**
   * Add files to pending list with auto-populated metadata
   */
  ADD_FILES_TO_PENDING({ commit, state }, files) {
    const newPendingFiles = files.map(file => {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      return {
        file,
        fileName: file.name,
        documentNumber: nameWithoutExt,
        documentTitle: nameWithoutExt,
        documentTypeId: state.selectedDocumentTypeId,
        revisionNumber: '0',
        isValid: true,
        errors: [],
      };
    });

    commit('ADD_PENDING_FILES', newPendingFiles);
    debugLog('[DocumentImports]', 'Added pending files:', newPendingFiles.length);
  },

  /**
   * Apply bulk document type to selected files
   */
  APPLY_BULK_DOCUMENT_TYPE({ commit, state }, documentTypeId) {
    state.selectedFileIndexes.forEach(index => {
      commit('UPDATE_PENDING_FILE', {
        index,
        updates: { documentTypeId },
      });
    });
    debugLog('[DocumentImports]', 'Applied bulk type to', state.selectedFileIndexes.length, 'files');
  },

  /**
   * Apply bulk revision to selected files
   */
  APPLY_BULK_REVISION({ commit, state }, revisionNumber) {
    state.selectedFileIndexes.forEach(index => {
      commit('UPDATE_PENDING_FILE', {
        index,
        updates: { revisionNumber },
      });
    });
    debugLog('[DocumentImports]', 'Applied bulk revision to', state.selectedFileIndexes.length, 'files');
  },

  /**
   * Open upload dialog
   */
  OPEN_UPLOAD_DIALOG({ commit }) {
    commit('RESET_UPLOAD_STATE');
    commit('SET_UPLOAD_DIALOG_OPEN', true);
  },

  /**
   * Close upload dialog
   */
  CLOSE_UPLOAD_DIALOG({ commit }) {
    commit('SET_UPLOAD_DIALOG_OPEN', false);
    commit('RESET_UPLOAD_STATE');
  },
};
